import { Button, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddButton from "../../../components/Buttons/AddButton";
import DeleteButton from "../../../components/Buttons/DeleteButton";
import { Desc, Subtitle, Title } from "../../../components/Desc";
import ProgressIndicator from "../../../components/ProgressIndicator";
import strings from "../../../config/localization";
import {
  useAllocatedResourceDetailsMutation,
  useReplaceResourceAllocationsMutation,
} from "../../../services/appln";
import SelectResources from "./SelectResources";

interface Resource {
  id: string;
  resourceTypeId: string;
  resourceTypeName: string;
  extSerialNo: string;
}

export default function ResourceAllocation() {
  const navigate = useNavigate();

  const params = useParams();
  console.log("ResourceAllocation, params:", params);

  const [selectDialogOpen, setSelectDialogOpen] = React.useState(false);

  const [replaceAllocation, replaceAllocationResp] =
    useReplaceResourceAllocationsMutation();

  const bandobastContext = JSON.parse(localStorage.getItem("bandobast")!);
  const timeslotContext = JSON.parse(localStorage.getItem("timeslot")!);
  const locationContext = JSON.parse(localStorage.getItem("location")!);

  const [getDetails, getDetailsParams] = useAllocatedResourceDetailsMutation();

  const [allocatedResources, setAllocatedResources] = React.useState<
    Resource[]
  >([]);

  const [availableResources, setAvailableResources] = React.useState<
    Resource[]
  >([]);

  React.useEffect(() => {
    getDetails({
      bandobastId: params.bandobastId,
      timeslotId: params.timeslotId,
      body: {
        resourceTypeId: params.resourceTypeId,
        locationId: params.locationId,
      },
    });
  }, []);

  React.useEffect(() => {
    if (getDetailsParams.isSuccess) {
      console.log("ResourceAllocation, getDetailsParams:", getDetailsParams);
      setAllocatedResources([...getDetailsParams.data.allocated]);
      setAvailableResources([...getDetailsParams.data.available]);
    }
  }, [getDetailsParams]);

  React.useEffect(() => {
    if (replaceAllocationResp.isSuccess) {
      navigate(-1);
    }
  }, [replaceAllocationResp]);

  const moveToAvailable = (index: number) => {
    // remove from allocated and move to available
    const resources = allocatedResources.splice(index, 1);
    setAllocatedResources([...allocatedResources]);
    const availableResourcesUpdated = [...availableResources, ...resources];
    console.log(
      "moveToAvailable, availableResourcesUpdated:",
      availableResourcesUpdated,
    );
    setAvailableResources(availableResourcesUpdated);
  };

  const columns: ColumnsType<Resource> = [
    {
      title: strings.resourceType,
      dataIndex: "resourceTypeName",
      key: "resourceTypeName",
    },
    {
      title: strings.extSerialNo,
      dataIndex: "externalSerialNo",
      key: "externalSerialNo",
    },
    {
      title: "",
      dataIndex: "delete",
      key: "delete",
      render: (text, record, index) => (
        <DeleteButton
          label={strings.remove}
          onClickHandler={() => {
            moveToAvailable(index);
          }}
        />
      ),
    },
  ];

  const countNum = locationContext.count;
  console.log("countNum", countNum);
  console.log("allocatedPersonnel.length=", allocatedResources.length);
  console.log("Equals", allocatedResources.length == countNum);

  return (
    <Box>
      {(getDetailsParams.isLoading || replaceAllocationResp.isLoading) && (
        <ProgressIndicator />
      )}
      <Stack spacing={3}>
        <Title text={strings.resourceAllocation} />
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Desc label={strings.bandobast} value={bandobastContext.name} />
            <Desc label={strings.timeslot} value={timeslotContext.name} />
            <Desc label={strings.location} value={locationContext.name} />
            <Desc
              label={strings.resourceType}
              value={locationContext.resourceTypeName}
            />
            <Desc
              label={strings.assessmentCount}
              value={locationContext.count}
            />
          </Grid>
        </Grid>
        <Stack spacing={0}>
          <Subtitle text={strings.allocatedResources} />
          <Grid item xs={12}>
            <AddButton
              onClickHandler={() => {
                setSelectDialogOpen(true);
              }}
              disabled={allocatedResources.length >= countNum}
            />
          </Grid>
          <SelectResources
            resources={availableResources}
            onSubmitHandler={(resources) => {
              setSelectDialogOpen(false);

              const pendingCount = countNum - allocatedResources.length;

              // if extra entries are selected, ignore them.
              resources = resources.slice(0, pendingCount);

              // only add upto pendingCount to the set
              const resourceIds = new Set<string>(resources.map((e) => e.id));
              const availableResourcesCopy = availableResources.filter(
                (e) => !resourceIds.has(e.id),
              );
              setAvailableResources(availableResourcesCopy);

              setAllocatedResources((prev: any) => {
                return [...prev, ...resources];
              });
            }}
            state={selectDialogOpen}
          />
          <Table
            virtual
            scroll={{ y: 400 }}
            columns={columns}
            dataSource={allocatedResources}
            pagination={false}
            rowKey="id"
          />
        </Stack>
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={() => {
              replaceAllocation({
                bandobastId: params.bandobastId,
                timeslotId: params.timeslotId,
                body: {
                  locationId: params.locationId,
                  resourceTypeId: locationContext.resourceTypeId,
                  resourceIds: allocatedResources.map((e: Resource) => e.id),
                },
              });
            }}
          >
            {strings.save}
          </Button>
        </Grid>
      </Stack>
    </Box>
  );
}
